import React, {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
} from 'react';

import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Banner, Button } from '@pushwoosh/dumb-components';
import { Paragraph } from '@pushwoosh/kit-typography';

import { H1 } from '~/src/ui-kit';
import { Lang } from '~/src/polyglot';
import { trackEvent } from '~/src/helpers/trackEvent';
import {
  dismissPasskeyPrompt,
  registerPasskey,
  type DismissDuration,
} from '~/src/helpers/passkey';

import { ButtonsBox, StyledContentBox } from './styles';

// Resuming the original OAuth2 flow: AuthorizeHandler keeps
// session.ClientRequestData across the interstitial round-trip, so a bare
// /authorize with no query params is enough to pick up where we left off.
const RESUME_URL = '/authorize';

export function SetupPasskey(): ReactElement | null {
  const [errorMessage, setErrorMessage] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [dismissing, setDismissing] = useState<DismissDuration | null>(null);

  // Browsers without WebAuthn (old Safari, old Edge, etc.) can't enroll —
  // fire a long dismissal so the interstitial stops showing and punt the
  // user straight to /authorize. No visible flash for them in practice:
  // the /authorize redirect lands immediately.
  useEffect(() => {
    if (browserSupportsWebAuthn()) return;
    trackEvent({
      name: 'PASSKEY_PROMPT_UNSUPPORTED',
      properties: {},
    });
    dismissPasskeyPrompt('long')
      .catch(() => { /* cookie still not set; user will see this page once more next time, tolerable */ })
      .finally(() => { document.location.href = RESUME_URL; });
  }, []);

  useEffect(() => {
    trackEvent({
      name: 'PASSKEY_PROMPT_SHOWN',
      properties: {},
    });
  }, []);

  const onAdd = useCallback(async (): Promise<void> => {
    setErrorMessage('');
    setIsRegistering(true);
    trackEvent({
      name: 'PASSKEY_PROMPT_ACCEPTED',
      properties: {},
    });
    try {
      await registerPasskey();
      trackEvent({
        name: 'PASSKEY_PROMPT_ENROLLED',
        properties: {},
      });
      document.location.href = RESUME_URL;
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : String(err));
      setIsRegistering(false);
    }
  }, []);

  const onDismiss = useCallback(async (duration: DismissDuration): Promise<void> => {
    setErrorMessage('');
    setDismissing(duration);
    trackEvent({
      name: 'PASSKEY_PROMPT_DISMISSED',
      properties: { duration },
    });
    try {
      await dismissPasskeyPrompt(duration);
    } catch {
      // If the dismiss cookie didn't land we'd show this screen again on
      // the next login — mildly annoying, not worth blocking the OAuth2
      // flow. Proceed to /authorize regardless.
    }
    document.location.href = RESUME_URL;
  }, []);

  if (!browserSupportsWebAuthn()) {
    return null; // redirect is in-flight in the effect above
  }

  return (
    <StyledContentBox>
      <H1><Lang message="SETUP_PASSKEY.TITLE" /></H1>
      <Paragraph>
        <Lang message="SETUP_PASSKEY.DESCRIPTION" />
      </Paragraph>

      {!!errorMessage && <Banner type="error" title={errorMessage} />}

      <ButtonsBox>
        <Button
          type="button"
          color="primary"
          isLoading={isRegistering}
          onClick={onAdd}
        >
          <Lang message="SETUP_PASSKEY.BUTTON.ADD" />
        </Button>
        <Button
          type="button"
          color="secondary"
          isLoading={dismissing === 'short'}
          onClick={(): Promise<void> => onDismiss('short')}
        >
          <Lang message="SETUP_PASSKEY.BUTTON.SKIP" />
        </Button>
        <Button
          type="button"
          color="secondary"
          view="ghost"
          isLoading={dismissing === 'long'}
          onClick={(): Promise<void> => onDismiss('long')}
        >
          <Lang message="SETUP_PASSKEY.BUTTON.NEVER" />
        </Button>
      </ButtonsBox>
    </StyledContentBox>
  );
}
